import React, { useState, useEffect, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import styles from "./importDataReview.module.css"
import Button from "../../../../components/button/button"
import HeaderTitle from "../../../../components/header/headerTitle/headerTitle"
import PlanLimitBanner from "../../../../components/planLimitBanner/planLimitBanner"
import PlanChangeModal from "../../../../components/planChangeModal/planChangeModal"
import { uploadFile, resolveStudentStatus } from "../../../../services/import"
import { getPlanStatus } from "../../../../services/plan"
import { getMantenedoraWithPlans, updateMantenedora } from "../../../../services/mantenedora"
import { useImportFlow } from "../../../../context/ImportFlowContext"
import { getExpectedColumns } from "../../../../utils/importColumns"
import { FILES_MAPPING } from "../../../../utils/pagesMapper"
import DownloadIcon from "../../../../assets/icons/download.svg"

const ImportDataReview = () => {
  const navigate = useNavigate()
  const { importData, clearImportData } = useImportFlow()

  const [columnMapping, setColumnMapping] = useState({})
  const [activeHeaderSelect, setActiveHeaderSelect] = useState(null)
  const headerSelectRef = useRef(null)
  const [isUploading, setIsUploading] = useState(false)
  const [missingFieldsWarning, setMissingFieldsWarning] = useState(null)
  const [importError, setImportError] = useState(null)
  const [backgroundInfo, setBackgroundInfo] = useState(null)
  const [statusMappingInfo, setStatusMappingInfo] = useState(null)
  const [statusMappingValues, setStatusMappingValues] = useState({})
  const [isResolvingStatus, setIsResolvingStatus] = useState(false)
  const [planLimit, setPlanLimit] = useState(null)
  /** true = consultar e aplicar mapeamentos de status salvos no armazenamento (blob). false = não usar esse arquivo. */
  const [useStudentStatusMemory, setUseStudentStatusMemory] = useState(true)

  // Fluxo de mudança de plano disparado pelo "Fazer upgrade" do PlanLimitBanner.
  // Reutiliza o mesmo modal da tela de Mantenedora.
  const [planChange, setPlanChange] = useState(null) // { step, mantenedoraId, fromPlan, toPlan }
  const [planChangeLoading, setPlanChangeLoading] = useState(false)

  useEffect(() => {
    if (!importData) {
      navigate('/app/importar-dados', { replace: true })
      return
    }
    setColumnMapping(importData.columnMapping || {})
  }, [importData, navigate])

  // Carrega o status do plano contratado pela mantenedora para essa instituicao
  // assim que a tela monta — permite alertar o usuario ANTES de iniciar o upload.
  useEffect(() => {
    if (!importData?.institutionId) return
    let cancelled = false
    ;(async () => {
      const info = await getPlanStatus(importData.institutionId)
      if (!cancelled) setPlanLimit(info)
    })()
    return () => { cancelled = true }
  }, [importData?.institutionId])

  useEffect(() => {
    if (!activeHeaderSelect) return
    const handleClickOutside = (e) => {
      if (headerSelectRef.current && !headerSelectRef.current.contains(e.target)) {
        setActiveHeaderSelect(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [activeHeaderSelect])

  const goBack = useCallback(() => {
    clearImportData()
    navigate('/app/importar-dados')
  }, [clearImportData, navigate])

  if (!importData) return null

  const {
    file,
    fileType,
    previewData,
    validationResult,
    templateColumns,
    institutionId
  } = importData

  const fileInfo = FILES_MAPPING[fileType]

  // Abre o fluxo de mudança de plano. O catálogo da mantenedora traz
  // id/valores; o planLimit traz a capacidade (maxActiveStudents) de cada
  // plano de upgrade. Cruzamos os dois e oferecemos para o usuário escolher
  // entre os planos que ATENDEM a necessidade projetada do arquivo.
  const handleUpgradeClick = async () => {
    const m = await getMantenedoraWithPlans()
    if (!m?.id || !Array.isArray(m.plans)) {
      toast.error("Não foi possível carregar os planos. Tente novamente.")
      return
    }
    const fromPlan = m.plans.find(p => p.id === m.planId) || null

    const upgrades = Array.isArray(planLimit?.availableUpgradePlans)
      ? planLimit.availableUpgradePlans
      : []
    const merged = upgrades
      .map(u => {
        const cat =
          m.plans.find(p => p.id === u.id) ||
          m.plans.find(p => p.name === u.name)
        return cat ? { ...cat, maxActiveStudents: u.maxActiveStudents } : null
      })
      .filter(Boolean)

    if (merged.length === 0) {
      toast.error("Nenhum plano de upgrade disponível no catálogo.")
      return
    }

    // Planos que comportam o total projetado após este arquivo. Se nenhum
    // comporta, oferecemos todos os de upgrade (o usuário decide).
    const projected = planLimit?.projectedActiveStudentsAfterImport
    const viable =
      projected != null
        ? merged.filter(
            p => p.maxActiveStudents != null && p.maxActiveStudents >= projected
          )
        : merged
    const options = viable.length > 0 ? viable : merged

    // Sempre exibe as opções para o usuário escolher — mesmo quando há
    // apenas um plano elegível.
    setPlanChange({ step: "select", mantenedoraId: m.id, fromPlan, options, projected })
  }

  const handleSelectPlan = (plan) => {
    setPlanChange(prev => (prev ? { ...prev, step: "confirm", toPlan: plan } : prev))
  }

  const handleConfirmPlanChange = async () => {
    if (!planChange?.mantenedoraId || !planChange?.toPlan?.id) return
    setPlanChangeLoading(true)
    try {
      await updateMantenedora(planChange.mantenedoraId, {
        planId: planChange.toPlan.id
      })
      setPlanChange(prev => (prev ? { ...prev, step: "success" } : prev))
    } catch (error) {
      toast.error(error.message || "Erro ao alterar o plano.")
    } finally {
      setPlanChangeLoading(false)
    }
  }

  const handleCloseConfirmPlanChange = () => {
    if (planChangeLoading) return
    setPlanChange(null)
  }

  // Após o upgrade, o limite mudou: volta para a revisão com o status do
  // plano atualizado para o usuário reenviar o arquivo.
  const handleCloseSuccessPlanChange = async () => {
    setPlanChange(null)
    setImportError(null)
    const info = await getPlanStatus(institutionId)
    setPlanLimit(info || null)
  }

  const planChangeModalEl = (
    <PlanChangeModal
      step={planChange?.step || null}
      fromPlan={planChange?.fromPlan}
      toPlan={planChange?.toPlan}
      options={planChange?.options}
      projected={planChange?.projected}
      loading={planChangeLoading}
      onSelect={handleSelectPlan}
      onConfirm={handleConfirmPlanChange}
      onCancel={handleCloseConfirmPlanChange}
      onCloseSuccess={handleCloseSuccessPlanChange}
    />
  )

  const handleHeaderMapping = (templateCol, fileCol) => {
    if (!fileCol) {
      setColumnMapping(prev => {
        const newMapping = { ...prev }
        const existingFileCol = Object.keys(newMapping).find(k => newMapping[k] === templateCol)
        if (existingFileCol) delete newMapping[existingFileCol]
        return newMapping
      })
    } else {
      setColumnMapping(prev => {
        const newMapping = { ...prev }
        const existingFileCol = Object.keys(newMapping).find(k => newMapping[k] === templateCol)
        if (existingFileCol) delete newMapping[existingFileCol]
        newMapping[fileCol] = templateCol
        return newMapping
      })
    }
    setActiveHeaderSelect(null)
  }

  const checkMissingFieldsAndProceed = async () => {
    const expectedColumns = getExpectedColumns(fileType)
    const mappedTemplateColumns = Object.values(columnMapping).filter(Boolean)
    const unmappedTemplateColumns = expectedColumns.filter(col => !mappedTemplateColumns.includes(col))

    if (unmappedTemplateColumns.length > 0) {
      setMissingFieldsWarning({
        mappedFields: mappedTemplateColumns,
        unmappedFields: unmappedTemplateColumns
      })
      return
    }

    await proceedWithUpload()
  }

  const proceedWithUpload = async () => {
    try {
      toast.dismiss()
      setIsUploading(true)

      const response = await uploadFile({
        file,
        institutionId,
        uploadType: fileInfo.url,
        columnMapping,
        ignoreSavedStudentStatusMappings:
          fileType === "aluno" ? !useStudentStatusMemory : false
      })
      setIsUploading(false)

      if (response?.planLimit) {
        setPlanLimit(response.planLimit)
      }

      if (response?.isBackground) {
        // Para o fluxo background, o backend ainda nao processou o arquivo:
        // consulta o status atual do plano para refletir o estado pre-import
        // no banner enquanto o usuario aguarda a notificacao final.
        const info = await getPlanStatus(institutionId)
        if (info) setPlanLimit(info)

        setBackgroundInfo({
          message: response.message || "O arquivo está sendo processado em segundo plano. Você será notificado quando o processamento finalizar."
        })
        return
      }

      if (response?.needsStatusMapping) {
        const initialValues = {}
        response.unresolvedStudentStatuses.forEach(val => { initialValues[val] = undefined })
        setStatusMappingValues(initialValues)
        setStatusMappingInfo({
          fileId: response.fileId,
          unresolvedStatuses: response.unresolvedStudentStatuses,
          importedData: response.data
        })
        return
      }

      toast.success("Arquivo enviado com sucesso!")
      goBack()
    } catch (error) {
      setIsUploading(false)

      if (Array.isArray(error) && error.planLimit) {
        setPlanLimit(error.planLimit)
      }

      let errorMessages = []
      if (Array.isArray(error)) {
        errorMessages = error
      } else if (typeof error === 'string') {
        errorMessages = [error]
      } else if (error?.message) {
        errorMessages = [error.message]
      } else {
        errorMessages = ["Falha ao enviar o arquivo. Por favor, tente novamente."]
      }

      setImportError({ messages: errorMessages })
    }
  }

  const handleSend = () => {
    if (!validationResult?.isValid) {
      if (Object.keys(columnMapping).length === 0) {
        toast.error("Por favor, mapeie as colunas antes de enviar o arquivo")
        return
      }
    }
    checkMissingFieldsAndProceed()
  }

  // Mapeamento reverso: templateColumn -> fileColumn (ignora entradas vazias)
  const reverseMapping = {}
  Object.entries(columnMapping).forEach(([fileCol, templateCol]) => {
    if (fileCol && templateCol) {
      reverseMapping[templateCol] = fileCol
    }
  })

  const unmappedTemplateColumns = templateColumns.filter(col => !reverseMapping[col])
  const allTemplateColumnsMapped = unmappedTemplateColumns.length === 0
  const fileTypeName = fileInfo?.modalTile?.replace("Enviar arquivo ", "").replace("Enviar arquivo de ", "") || fileType

  // Warning modal (campos faltantes)
  if (missingFieldsWarning) {
    return (
      <div className={styles.warningOverlay}>
        <div className={styles.warningModal}>
          <div className={styles.warningIcon}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" fill="#e89a0c"/>
            </svg>
          </div>
          <h3 className={styles.warningTitle}>Atenção: o cabeçalho do arquivo está diferente do esperado pelo sistema.</h3>
          <p className={styles.warningMessage}>
            As seguintes colunas não foram encontradas e ficarão sem dados:
          </p>
          <div className={styles.warningFieldsList}>
            <div className={styles.warningFieldsSection}>
              <strong className={styles.warningFieldsLabelMissing}>Colunas ausentes:</strong>
              <div className={styles.warningBadges}>
                {missingFieldsWarning.unmappedFields.map((field, idx) => (
                  <span key={idx} className={styles.warningBadgeUnmapped}>{field}</span>
                ))}
              </div>
            </div>
            <div className={styles.warningFieldsSection}>
              <strong className={styles.warningFieldsLabel}>Colunas identificadas:</strong>
              <div className={styles.warningBadges}>
                {missingFieldsWarning.mappedFields.map((field, idx) => (
                  <span key={idx} className={styles.warningBadgeMapped}>{field}</span>
                ))}
              </div>
            </div>
          </div>
          {fileType === 'faturamento' && missingFieldsWarning.unmappedFields.includes('Data_Vencimento') && (
            <div className={styles.warningImpactNote}>
              <strong>Importante:</strong> Sem o campo <strong>Data_Vencimento</strong>, não será possível filtrar os indicadores financeiros por data (período).
            </div>
          )}
          <p className={styles.warningMessage}>Deseja prosseguir com a importação mesmo assim?</p>
          <div className={styles.warningActions}>
            <Button
              text="Voltar e corrigir"
              variation="secondary"
              size="big"
              onClick={() => setMissingFieldsWarning(null)}
              styling={{ flex: 1, justifyContent: 'center' }}
            />
            <Button
              text="Continuar mesmo assim"
              variation="primary"
              size="big"
              onClick={() => {
                setMissingFieldsWarning(null)
                proceedWithUpload()
              }}
              styling={{ flex: 1, justifyContent: 'center' }}
            />
          </div>
        </div>
      </div>
    )
  }

  // Error modal
  if (importError) {
    // Quando o erro é o limite do plano, o PlanLimitBanner já explica tudo e
    // traz as ações (upgrade / ajustar). Não repetimos a mensagem do backend
    // nem sugerimos "corrigir o formato" — o arquivo não tem problema.
    const isPlanLimitError = !!planLimit?.limitExceeded

    return (
      <div className={styles.errorOverlay}>
        <div className={styles.errorModal}>
          <div className={styles.errorIcon}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#dc3545"/>
            </svg>
          </div>
          <h3 className={styles.errorTitle}>
            {isPlanLimitError
              ? "A importação precisa de um ajuste"
              : "Erro na importação"}
          </h3>

          {isPlanLimitError ? (
            <>
              <PlanLimitBanner
                planLimit={planLimit}
                variant="exceeded-only"
                onUpgradeClick={handleUpgradeClick}
                onAdjustFileClick={goBack}
              />
              <p className={styles.errorSuggestion}>
                Nenhum dado foi importado ainda — escolha uma das opções acima para continuar.
              </p>
              <div className={styles.errorActions}>
                <Button
                  text="Voltar e ajustar o arquivo"
                  variation="primary"
                  size="big"
                  onClick={goBack}
                  styling={{ width: '100%', justifyContent: 'center' }}
                />
              </div>
            </>
          ) : (
            <>
              <div className={styles.errorList}>
                {importError.messages.map((msg, idx) => (
                  <p key={idx} className={styles.errorItem}>{msg}</p>
                ))}
              </div>
              <p className={styles.errorSuggestion}>
                Verifique se o arquivo está no formato correto. Você pode baixar o modelo de planilha para o tipo <strong>{fileTypeName}</strong>.
              </p>
              <div className={styles.errorActions}>
                <a href={`/model-files/${fileInfo.file}`} download style={{ width: '100%' }}>
                  <Button
                    text="Baixar modelo de planilha"
                    variation="secondary"
                    size="big"
                    icon={DownloadIcon}
                    styling={{ width: '100%', justifyContent: 'center' }}
                  />
                </a>
                <Button
                  text="Voltar"
                  variation="primary"
                  size="big"
                  onClick={goBack}
                  styling={{ width: '100%', justifyContent: 'center' }}
                />
              </div>
            </>
          )}
        </div>
        {planChangeModalEl}
      </div>
    )
  }

  const handleStatusMappingSubmit = async () => {
    const allMapped = statusMappingInfo.unresolvedStatuses.every(
      val => statusMappingValues[val] !== undefined
    )
    if (!allMapped) {
      toast.warning("Por favor, selecione uma opção para todos os valores.")
      return
    }

    try {
      setIsResolvingStatus(true)
      const mappings = {}
      for (const [raw, mapped] of Object.entries(statusMappingValues)) {
        mappings[raw] = mapped === 'empty' ? null : mapped
      }

      const resolveResponse = await resolveStudentStatus({
        fileId: statusMappingInfo.fileId,
        mappings,
        institutionId
      })

      if (resolveResponse?.planLimit) {
        setPlanLimit(resolveResponse.planLimit)
      }

      toast.success("Arquivo enviado com sucesso! Status dos alunos atualizados.")
      goBack()
    } catch (error) {
      setIsResolvingStatus(false)
      toast.error("Erro ao atualizar status. Tente novamente.")
    }
  }

  // Status mapping modal
  if (statusMappingInfo) {
    const allMapped = statusMappingInfo.unresolvedStatuses.every(
      val => statusMappingValues[val] !== undefined
    )

    return (
      <div className={styles.warningOverlay}>
        <div className={styles.statusMappingModal}>
          <div className={styles.warningIcon}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" fill="#e89a0c"/>
            </svg>
          </div>
          <h3 className={styles.warningTitle}>Status do aluno não identificado</h3>
          <p className={styles.statusMappingDescription}>
            Os dados foram importados, porém alguns valores do campo <strong>Status do Aluno</strong> não
            foram identificados automaticamente. Indique como cada valor deve ser salvo:
          </p>

          <PlanLimitBanner
            planLimit={planLimit}
            variant="exceeded-only"
            onUpgradeClick={handleUpgradeClick}
          />
          {planChangeModalEl}

          <table className={styles.valueMappingTable}>
            <thead>
              <tr>
                <th>Valor encontrado</th>
                <th>Salvar como</th>
              </tr>
            </thead>
            <tbody>
              {statusMappingInfo.unresolvedStatuses.map((rawValue) => (
                <tr key={rawValue}>
                  <td className={styles.rawValueCell}>{rawValue}</td>
                  <td>
                    <div className={styles.radioGroup}>
                      {[
                        { value: 'Ativo', label: 'Ativo' },
                        { value: 'Inativo', label: 'Inativo' },
                        { value: 'empty', label: 'Vazio' }
                      ].map(option => (
                        <label
                          key={option.value}
                          className={`${styles.radioLabel} ${statusMappingValues[rawValue] === option.value ? styles.radioLabelSelected : ''}`}
                        >
                          <input
                            type="radio"
                            name={`status-${rawValue}`}
                            value={option.value}
                            checked={statusMappingValues[rawValue] === option.value}
                            onChange={() => setStatusMappingValues(prev => ({ ...prev, [rawValue]: option.value }))}
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.statusMappingNote}>
            <strong>Atenção:</strong> Deixar o campo vazio pode impactar os indicadores que dependem do status do aluno.
          </div>

          <div className={styles.warningActions}>
            <Button
              text="Cancelar"
              variation="secondary"
              size="big"
              onClick={goBack}
              styling={{ flex: 1, justifyContent: 'center' }}
            />
            <Button
              text={isResolvingStatus ? "Salvando..." : "Confirmar e salvar"}
              variation="primary"
              size="big"
              onClick={handleStatusMappingSubmit}
              disabled={!allMapped || isResolvingStatus}
              styling={{ flex: 1, justifyContent: 'center' }}
            />
          </div>
        </div>
      </div>
    )
  }

  // Background processing info
  if (backgroundInfo) {
    return (
      <div className={styles.backgroundOverlay}>
        <div className={styles.backgroundModal}>
          <div className={styles.backgroundIcon}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15v-2h2v2h-2zm0-4V7h2v6h-2z" fill="#0962ad"/>
            </svg>
          </div>
          <h3 className={styles.backgroundTitle}>Arquivo em processamento</h3>
          <p className={styles.backgroundMessage}>{backgroundInfo.message}</p>

          <PlanLimitBanner
            planLimit={planLimit}
            variant="exceeded-only"
            onUpgradeClick={handleUpgradeClick}
          />

          <Button
            text="OK"
            variation="primary"
            size="big"
            onClick={goBack}
            styling={{ width: '100%', justifyContent: 'center' }}
          />
        </div>
        {planChangeModalEl}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <HeaderTitle title={`Revisão — ${fileTypeName}`}>
        <div className={styles.headerActions}>
          <Button
            text="Voltar"
            variation="secondary"
            size="normal"
            onClick={goBack}
          />
          <Button
            text={isUploading ? "Enviando..." : "Enviar Arquivo"}
            variation="primary"
            size="normal"
            onClick={handleSend}
            disabled={isUploading}
          />
        </div>
      </HeaderTitle>

      <PlanLimitBanner
        planLimit={planLimit}
        variant="exceeded-only"
        onUpgradeClick={handleUpgradeClick}
        onAdjustFileClick={goBack}
      />
      {planChangeModalEl}

      {/* Validation banner */}
      {validationResult && (
        <div className={`${styles.validationBanner} ${allTemplateColumnsMapped ? styles.validationSuccess : styles.validationWarning}`}>
          <span className={styles.validationBannerIcon}>
            {allTemplateColumnsMapped ? '✓' : '⚠'}
          </span>
          <div className={styles.validationBannerContent}>
            <h4 className={styles.validationBannerTitle}>
              {allTemplateColumnsMapped
                ? "Validação bem-sucedida"
                : "Alguns campos do modelo não foram encontrados no seu arquivo"}
            </h4>
            {allTemplateColumnsMapped
              ? <p className={styles.validationBannerMessage}>Todos os campos foram mapeados com sucesso.</p>
              : <p className={styles.validationBannerMessage}>
                  Não encontramos colunas no seu arquivo que correspondam a: {unmappedTemplateColumns.map(c => `'${c}'`).join(', ')}.
                  Para mapear manualmente, clique no cabeçalho destacado na tabela "Template do Sistema". Se enviar sem mapear, esses campos ficarão vazios na importação.
                </p>
            }
          </div>
        </div>
      )}

      {/* Aviso faturamento: indicadores financeiros podem ficar incompletos */}
      {fileType === 'faturamento' && (
        <div className={`${styles.validationBanner} ${styles.validationWarning}`}>
          <span className={styles.validationBannerIcon}>ℹ</span>
          <div className={styles.validationBannerContent}>
            <h4 className={styles.validationBannerTitle}>Dados de faturamento e indicadores financeiros</h4>
            <p className={styles.validationBannerMessage}>
              Estes dados alimentam os indicadores financeiros do sistema. Na falta de algum campo, os indicadores podem ficar incompletos ou sem dados.
            </p>
          </div>
        </div>
      )}

      {fileType === 'aluno' && (
        <div className={styles.importOptionBanner}>
          <div className={styles.importOptionHeaderRow}>
            <div className={styles.importOptionCopy}>
              <h4 className={styles.importOptionTitle}>Memória de mapeamento de status</h4>
              <p className={styles.importOptionHint}>
                Quando <strong>ativada</strong>, o sistema usa o arquivo de mapeamentos salvos no armazenamento.
                Ao <strong>desligar</strong>, essa memória é ignorada e apenas a conversão padrão (e a resolução automática)
                serão aplicadas — útil se os textos de status no arquivo mudaram.
              </p>
            </div>
            <div className={styles.toggleControl}>
              <span
                className={styles.toggleStateLabel}
                id="student-status-memory-label"
              >
                {useStudentStatusMemory ? 'Memória ativa' : 'Memória desligada'}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={useStudentStatusMemory}
                aria-labelledby="student-status-memory-label"
                className={`${styles.toggleTrack} ${useStudentStatusMemory ? styles.toggleTrackOn : ''}`}
                onClick={() => setUseStudentStatusMemory((v) => !v)}
              >
                <span className={styles.toggleThumb} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Split panel - duas tabelas */}
      <div className={styles.splitPanel}>
        {/* Left: arquivo original do usuario */}
        <div className={styles.tablePanel}>
          <div className={styles.tablePanelHeader}>
            <div>
              <h3 className={styles.tablePanelTitle}>Arquivo Importado</h3>
              <p className={styles.tablePanelSubtitle}>
                {file.name} — {previewData.rows.length} linha(s)
                {previewData.isPartialRead && (
                  <span> (~{previewData.estimatedTotal.toLocaleString('pt-BR')} total estimado)</span>
                )}
              </p>
            </div>
          </div>

          <div className={styles.tableWrapper}>
            <table className={styles.previewTable}>
              <thead>
                <tr>
                  <th>#</th>
                  {previewData.headers.map((header, index) => (
                    <th key={index}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewData.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td className={styles.rowNumber}>{rowIndex + 1}</td>
                    {previewData.headers.map((header, colIndex) => (
                      <td key={colIndex}>
                        {row[header] || <span className={styles.emptyCell}>vazio</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right: template com dados mapeados */}
        <div className={styles.tablePanel}>
          <div className={styles.tablePanelHeader}>
            <div>
              <h3 className={styles.tablePanelTitle}>Template do Sistema</h3>
              <p className={styles.tablePanelSubtitle}>
                {templateColumns.length} colunas esperadas — dados mapeados pela IA
                {unmappedTemplateColumns.length > 0 && (
                  <span className={styles.unmappedCount}>
                    {' '}({unmappedTemplateColumns.length} sem mapeamento)
                  </span>
                )}
              </p>
            </div>
            <a
              href={`/model-files/${fileInfo.file}`}
              download
              className={styles.downloadLink}
            >
              <img src={DownloadIcon} alt="Download" />
              Baixar modelo
            </a>
          </div>

          <div className={styles.tableWrapper}>
            <table className={styles.previewTable}>
              <thead>
                <tr>
                  <th>#</th>
                  {templateColumns.map((col, index) => {
                    const isMapped = !!reverseMapping[col]
                    const isSelectOpen = activeHeaderSelect === col
                    const availableFileColumns = previewData.headers

                    return (
                      <th
                        key={index}
                        className={`${!isMapped ? styles.unmappedHeader : styles.mappedHeader} ${styles.clickableHeader}`}
                        onClick={() => !isSelectOpen && setActiveHeaderSelect(col)}
                      >
                        <span className={styles.headerName}>{col}</span>
                        {isMapped && (
                          <span className={styles.mappedFromBadge}>
                            ← {reverseMapping[col]}
                          </span>
                        )}
                        {!isMapped && !isSelectOpen && (
                          <span className={styles.unmappedBadge}>clique para mapear</span>
                        )}
                        {isSelectOpen && (
                          <div
                            className={styles.headerDropdown}
                            ref={headerSelectRef}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <select
                              className={styles.headerSelect}
                              value={reverseMapping[col] || ''}
                              onChange={(e) => handleHeaderMapping(col, e.target.value)}
                              autoFocus
                              onKeyDown={(e) => e.key === 'Escape' && setActiveHeaderSelect(null)}
                            >
                              <option value="">— Selecione —</option>
                              {availableFileColumns.map((fileCol, idx) => (
                                <option key={idx} value={fileCol}>{fileCol}</option>
                              ))}
                            </select>
                            {isMapped && (
                              <button
                                className={styles.headerRemoveBtn}
                                onClick={() => handleHeaderMapping(col, '')}
                                type="button"
                                title="Remover mapeamento"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        )}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {previewData.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td className={styles.rowNumber}>{rowIndex + 1}</td>
                    {templateColumns.map((col, colIndex) => {
                      const fileCol = reverseMapping[col]
                      const value = fileCol ? row[fileCol] : null
                      const isMapped = !!fileCol

                      return (
                        <td
                          key={colIndex}
                          className={!isMapped ? styles.unmappedCell : ''}
                        >
                          {isMapped
                            ? (value || <span className={styles.emptyCell}>vazio</span>)
                            : <span className={styles.unmappedCellText}>—</span>
                          }
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImportDataReview
